<template>
  <f7-page name="group-buy-home">
    <f7-navbar :title="$t('groupBuy.title')" back-link />
    <AppLayout>
      <template #main>
        <f7-card class="gb-intro" :padding="false">
          <f7-card-content class="gb-intro-content" :padding="false">
            <div class="gb-intro-title">{{ $t("groupBuy.intro") }}</div>
            <div class="gb-intro-sub">{{ $t("groupBuy.introSub") }}</div>
          </f7-card-content>
        </f7-card>

        <!-- Active campaigns -->
        <div class="gb-section">
          <div class="gb-section-head">
            <h2 class="gb-section-title">{{ $t("groupBuy.campaigns") }}</h2>
            <f7-button fill small @click="goCreate">
              + {{ $t("groupBuy.createCampaign") }}
            </f7-button>
          </div>

          <div v-if="loading" class="gb-center">
            <f7-preloader />
          </div>

          <div
            v-else-if="!groupCampaigns.length"
            class="gb-empty"
          >
            <p>{{ $t("groupBuy.noCampaigns") }}</p>
          </div>

          <div v-else class="gb-list space-y-2">
            <f7-link
              v-for="c in groupCampaigns"
              :key="c.id"
              class="display-block no-underline"
              :href="`/groups/${props.groupId}/group-buy/campaigns/${c.id}`"
            >
              <f7-card class="gb-card" :padding="false">
                <f7-card-content class="gb-card-content" :padding="false">
                  <div class="gb-card-top">
                    <div class="min-w-0 flex-1">
                      <h4 class="gb-card-title">{{ c.title }}</h4>
                      <p class="gb-card-sub">
                        {{ t("groupBuy.participants") }}:
                        {{ c.currentParticipantsCount }} /
                        {{ c.maximumParticipants }}
                      </p>
                    </div>
                    <f7-badge :style="{ color: statusMeta(c).color, background: statusBg(c) }" class="font-label-sm font-semibold">{{ $t(statusMeta(c).labelKey) }}</f7-badge>
                  </div>
                  <div class="gb-card-row">
                    <span class="gb-card-price">{{ priceLabel(c) }}</span>
                    <span class="gb-card-unit">
                      {{ t("groupBuy.perUnit", { n: c.currentParticipantCount }) }}
                    </span>
                  </div>
                </f7-card-content>
              </f7-card>
            </f7-link>
          </div>
        </div>

        <!-- Products -->
        <div class="gb-section">
          <div class="gb-section-head">
            <h2 class="gb-section-title">{{ $t("groupBuy.eligibleProducts") }}</h2>
          </div>

          <div v-if="!eligibleProducts.length" class="gb-empty">
            <p>{{ $t("groupBuy.noProducts") }}</p>
          </div>

          <div v-else class="gb-list space-y-2">
            <f7-card
              v-for="p in eligibleProducts"
              :key="p.id"
              class="gb-card"
              :padding="false"
            >
              <f7-card-content class="gb-card-content" :padding="false">
                <div class="gb-card-top">
                  <div class="min-w-0 flex-1">
                    <h4 class="gb-card-title truncate">{{ productName(p) }}</h4>
                    <p class="gb-card-sub">{{ fmtCfa(productPrice(p)) }}</p>
                  </div>
                  <f7-button raised round  fill small @click="goCreateProduct(p)">
                    {{ t("groupBuy.createFor") }}
                  </f7-button>
                </div>
              </f7-card-content>
            </f7-card>
          </div>
        </div>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { fmtCfa } from "../constants/listing-options.js";
import {
  useGroupBuyStore,
  GROUP_BUY_STATUS_META,
  groupBuyUnitPrice,
} from "../js/pinia-stores/groupBuy.js";
import { useMarketplaceStore } from "../js/pinia-stores/marketplace.js";
import { useMarketplaceProductStore } from "../js/pinia-stores/marketplaceProduct.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const props = defineProps({ groupId: { type: String, required: true }, group: { type: Object, default: null } });

const { t } = i18n.global;
const groupBuyStore = useGroupBuyStore();
const marketplaceStore = useMarketplaceStore();
const marketplaceProductStore = useMarketplaceProductStore();
const groupsStore = useGroupsStore();

const loading = ref(true);

const groupCampaigns = computed(() => groupBuyStore.groupCampaigns);
const eligibleProducts = computed(() =>
  groupBuyStore.eligibleProducts,
);

function statusMeta(c) {
  return GROUP_BUY_STATUS_META[c.status] || GROUP_BUY_STATUS_META.DRAFT;
}
function statusBg(c) {
  return colorMix(statusMeta(c).color, 12);
}

function priceLabel(c) {
  const unit = groupBuyUnitPrice(
    c.tiers || [],
    c.currentParticipantCount || 0,
  );
  return unit ? fmtCca(unit) : (c.startingPrice ? fmtCfa(c.startingPrice) : "");
}
function fmtCca(n) {
  return fmtCfa(n);
}
function productName(p) {
  return p.title || p.name || p.id;
}
function productPrice(p) {
  return p.price ?? p.unitPrice ?? 0;
}
function colorMix(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
}

function campaignHref(c) {
   f7.views.main.router.navigate(`/groups/${props.groupId}/group-buy/campaigns/${c.id}`, {props: { group: props.group, groupId: props.groupId ,campaign:c}});
}
function goCreate() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/group-buy/create`, {
    props: { groupId: props.groupId, group: props.group },
  });
}
function goCreateProduct(p) {
  f7.views.main.router.navigate(`/groups/${props.groupId}/group-buy/create/${p.id}`);
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
    marketplaceStore.fetchGroupMarketplace(props.groupId).catch(() => {}),
  ]);

  const shop = marketplaceStore.getGroupShop(props.groupId);
  const products = shop?.id ? await marketplaceProductStore.getByShop(shop.id).catch(() => []) : [];
  groupBuyStore.eligibleProducts =
    (Array.isArray(products) ? products : []).filter((p) => {
      if (p.groupBuyEnabled != null) return p.groupBuyEnabled;
      return true;
    }) || [];

  const campaigns = [];
  const ids = new Set();
  for (const p of groupBuyStore.eligibleProducts) {
    const list = await groupBuyStore.getCampaignsByProduct(p.id).catch(() => []);
    for (const c of list) {
      if (!ids.has(c.id)) {
        ids.add(c.id);
        campaigns.push(c);
      }
    }
  }
  groupBuyStore.groupCampaigns = campaigns;
  loading.value = false;
});
</script>

<style scoped>
.gb-intro {
  margin: 16px;
  box-shadow: none;
  border-radius: 12px;
  background: color-mix(in srgb, var(--f7-theme-color) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 16%, transparent);
}
.gb-intro-content {
  position: relative;
  padding: 16px;
}
.gb-intro-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}
.gb-intro-sub {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 6px;
}
.gb-section {
  padding: 0 16px 8px;
}
.gb-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 8px;
}
.gb-section-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}
.gb-list {
  display: flex;
  flex-direction: column;
}
.gb-card {
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  background: var(--f7-block-strong-bg-color, #fff);
  border: 1px solid color-mix(in srgb, #000 8%, transparent);
}
.gb-card-content {
  position: relative;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gb-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.gb-card-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: var(--f7-text-color, #333);
}
.gb-card-sub {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 2px 0 0;
}
.gb-card-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.gb-card-price {
  font-size: 15px;
  font-weight: 700;
  color: var(--f7-theme-color);
}
.gb-card-unit {
  font-size: 11.5px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.gb-center {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
.gb-empty {
  background: var(--f7-block-strong-bg-color, #fff);
  border: 1px solid color-mix(in srgb, #000 8%, transparent);
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
</style>